// Reusable dashboard footer blocks: fee chip lists, monthly earnings box and a
// plain total box, all theme-aware.
import { Badge, Box, Flex, Text, useColorModeValue } from "@chakra-ui/react";

/** Horizontal-scroll style shared by chip rows. */
const SCROLL_SX = {
  overflowX: "auto",
  "::-webkit-scrollbar": { height: "4px" },
  "::-webkit-scrollbar-thumb": { background: "rgba(163,174,208,0.4)", borderRadius: "10px" }
};

/**
 * Renders a horizontal, scrollable list of fee badges.
 * @param {Object} props - fees ([{symbol, amount}]), colorScheme
 * @returns {React.ReactElement}
 */
export function FeeChips({ fees, colorScheme }) {
  return (
    <Flex gap="6px" sx={SCROLL_SX} pb="2px" justify="center">
      {fees.map((fee, idx) => (
        <Badge key={idx} colorScheme={colorScheme} borderRadius="8px" px="8px" py="3px"
          fontSize="sm" flexShrink="0">
          {fee.symbol}{Number(fee.amount).toFixed(2)}
        </Badge>
      ))}
    </Flex>
  );
}

/**
 * Renders a labelled potential-fees section, or nothing when there are no fees.
 * @param {Object} props - fees ([{symbol, amount}]), label
 * @returns {React.ReactElement|null}
 */
export function PotentialFees({ fees, label }) {
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  if (!fees?.length) return null;
  return (
    <Box mt="12px" textAlign="center">
      <Text fontSize="xs" color={labelColor} mb="6px">{label}</Text>
      <FeeChips fees={fees} colorScheme="orange" />
    </Box>
  );
}

/**
 * Renders the monthly-earnings total box: green fee chips, or a "0" fallback.
 * @param {Object} props - fees ([{symbol, amount}]), label
 * @returns {React.ReactElement}
 */
export function MonthEarnings({ fees, label }) {
  const boxBg = useColorModeValue("secondaryGray.300", "navy.700");
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "white");
  return (
    <Box mt="auto" p="14px" borderRadius="14px" bg={boxBg} textAlign="center">
      <Text fontSize="xs" textTransform="uppercase" fontWeight="600" color={labelColor} mb="6px">
        {label}
      </Text>
      {fees?.length
        ? <FeeChips fees={fees} colorScheme="green" />
        : <Text fontSize="2xl" fontWeight="800" color={valueColor}>0</Text>}
    </Box>
  );
}

/**
 * Renders a simple total box with an uppercase label and a large value.
 * @param {Object} props - label, value
 * @returns {React.ReactElement}
 */
export function StatTotal({ label, value }) {
  const boxBg = useColorModeValue("secondaryGray.300", "navy.700");
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "white");
  return (
    <Box mt="auto" p="16px" borderRadius="14px" bg={boxBg} textAlign="center">
      <Text fontSize="xs" textTransform="uppercase" fontWeight="600" color={labelColor} mb="4px">
        {label}
      </Text>
      <Text fontSize="3xl" fontWeight="800" color={valueColor} lineHeight="1">{value}</Text>
    </Box>
  );
}
